import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { StatusBadge } from "@/pages/Dashboard";
import { Pencil, Download, Trash2, Plus, Film, Loader2 } from "lucide-react";

export default function Library() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([api.get("/projects"), api.get("/assets", { params: { kind: "source" } })])
      .then(([p, a]) => { setProjects(p.data); setAssets(a.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sourceName = (id) => assets.find((a) => a.id === id)?.name || "—";

  const download = (assetId) => {
    const token = localStorage.getItem("rf_token");
    window.open(`${API}/assets/${assetId}/download?auth=${token}`, "_blank");
  };

  const removeProject = async (p) => {
    try {
      if (p.output_asset_id) await api.delete(`/assets/${p.output_asset_id}`);
      toast.success("Removed processed output");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading)
    return <div className="flex h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;

  return (
    <div className="min-h-screen p-10">
      <div className="rf-fade-up flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF]">Library</p>
          <h1 className="mt-1 font-heading text-4xl font-bold">Your Projects</h1>
        </div>
        <button onClick={() => navigate("/import")} data-testid="new-import-btn" className="flex items-center gap-2 rounded-md bg-[#00E5FF] px-4 py-2.5 text-sm font-semibold text-black hover:bg-[#33EBFF]">
          <Plus className="h-4 w-4" /> Import Video
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="mt-16 flex flex-col items-center text-center">
          <Film className="h-12 w-12 text-gray-600" />
          <p className="mt-4 text-gray-400">No projects yet.</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <div key={p.id} data-testid={`project-card-${p.id}`} className="group overflow-hidden rounded-lg border border-white/10 bg-[#121212] transition-all hover:-translate-y-1 hover:border-[#00E5FF]/30">
              <div className="flex aspect-video items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a]">
                <Film className="h-10 w-10 text-gray-700" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="truncate font-medium">{p.name}</h3>
                  <StatusBadge status={p.status} />
                </div>
                <p className="mt-1 truncate text-xs text-gray-500">Source: {sourceName(p.source_asset_id)}</p>
                <div className="mt-4 flex items-center gap-2">
                  <button onClick={() => navigate(`/editor/${p.id}`)} data-testid={`edit-btn-${p.id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-white/10 py-2 text-sm font-medium hover:bg-white/5">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  {isAdmin && p.status === "ready" && p.output_asset_id && (
                    <>
                      <button onClick={() => download(p.output_asset_id)} data-testid={`download-btn-${p.id}`} title="Download" className="flex items-center justify-center rounded-md border border-white/10 p-2 text-[#00E5FF] hover:bg-white/5">
                        <Download className="h-4 w-4" />
                      </button>
                      <button onClick={() => removeProject(p)} data-testid={`delete-btn-${p.id}`} title="Remove output" className="flex items-center justify-center rounded-md border border-white/10 p-2 text-[#FF3B30] hover:bg-white/5">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
